import dayjs, { Dayjs } from 'dayjs';
import {
    AutocompleteOption,
    FormikPoolDatePicker,
    PoolAvatar,
    PoolButton,
    PoolFormikAutocomplete,
    PoolFormikTextfield,
    PoolLink,
    PoolRow,
    PoolTooltip,
    PoolTypography,
} from '@synergy/components-next';
import { Box, Grid, Stack, styled } from '@mui/material';
import { FocusEvent, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { StarIcon } from '@synergy/theme-next';
import { fields } from '@synergy/utils';
import { useFormikContext } from 'formik';

import { Contact } from '../../models/contact';
import { OrderDataFormValues } from './shopping-cart-page';
import {
    changeDefaultSettings,
    setAvisoContact,
    setCostCentre,
    setDeliveryAddress,
    setDesiredDeliveryDate,
    setInvoiceAddress,
    setOrderReference,
} from '../../store/shopping-cart-delivery-options-actions';
import {
    deliveryAddressesSelector,
    deliveryAvisoContactsSelector,
    deliveryOptionSelector,
    invoiceAddressesSelector,
} from '../../store/shopping-cart-selectors';
import { useAppDispatch, useAppSelector } from '../../../store/store';

const fieldNames = fields<OrderDataFormValues>();

const StarContainer = styled(PoolTooltip)(() => ({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 20,
    minHeight: 20,
    padding: 0.5,
    verticalAlign: 'middle',
    marginRight: 10,
}));

export const OrderData = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const deliveryOptions = useAppSelector(deliveryOptionSelector);
    const avisoContacts = useAppSelector(deliveryAvisoContactsSelector);
    const deliveryAddresses = useAppSelector(deliveryAddressesSelector);
    const invoiceAddresses = useAppSelector(invoiceAddressesSelector);
    const formik = useFormikContext<OrderDataFormValues>();
    const [selectedAvisoContact, setSelectedAvisoContact] = useState<Contact | null>(null);

    useEffect(() => {
        if (formik.values.avisoContactId) {
            const contact = deliveryOptions?.avisoContacts.find(c => c.id === formik.values.avisoContactId);
            setSelectedAvisoContact(contact ?? null);
        }
    }, [formik.values.avisoContactId, deliveryOptions?.avisoContacts]);

    if (!deliveryOptions) return null;

    const isDefaultDeliveryAddress =
        formik.values.deliveryAddressId &&
        deliveryOptions.defaults.deliveryAddressId === formik.values.deliveryAddressId;
    const isDefaultInvoiceAddress =
        formik.values.invoiceAddressId && deliveryOptions.defaults.invoiceAddressId === formik.values.invoiceAddressId;
    const isDefaultAvisoContact =
        (deliveryOptions.defaults.avisoContactId ?? -1) === (formik.values.avisoContactId ?? null);
    const isDefaultCostCentre =
        formik.values.costCentre !== '' && deliveryOptions.defaults.costCentre === formik.values.costCentre;

    const contactOptions: AutocompleteOption<number>[] = avisoContacts.map(contact => ({
        value: contact.id,
        displayValue: contact.fullName,
        endAdornment:
            contact.id === deliveryOptions.defaults.avisoContactId ? (
                <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                    <StarIcon />
                </StarContainer>
            ) : null,
    }));

    const deliveryAddressOptions: AutocompleteOption<number>[] =
        deliveryAddresses.map(address => ({
            value: address.id,
            displayValue: address.fullAddressWithTitle,
            endAdornment:
                address.id === deliveryOptions.defaults.deliveryAddressId ? (
                    <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                        <StarIcon />
                    </StarContainer>
                ) : null,
        })) ?? [];

    const invoiceAddressOptions: AutocompleteOption<number>[] =
        invoiceAddresses.map(address => ({
            value: address.id,
            displayValue: address.fullAddressWithTitle,
            endAdornment:
                address.id === deliveryOptions.defaults.invoiceAddressId ? (
                    <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                        <StarIcon />
                    </StarContainer>
                ) : null,
        })) ?? [];

    const handleAvisoContactChanged = (value: number | null): void => {
        dispatch(setAvisoContact(value));
    };

    const handleDeliveryAddressChanged = (value: number | null): void => {
        if (value !== null) {
            dispatch(setDeliveryAddress(value));

            const costCentre =
                deliveryOptions.deliveryAddresses.find(address => address.id === value)?.costCentre ?? '';
            dispatch(setCostCentre(costCentre));
            formik.setFieldValue(fieldNames.costCentre, costCentre);
        }
    };

    const handleInvoiceAddressChanged = (value: number | null): void => {
        if (value !== null) {
            dispatch(setInvoiceAddress(value));
        }
    };

    const handleCostCentreChanged = (e: FocusEvent<HTMLInputElement>): void => {
        dispatch(setCostCentre(e.target.value));
    };

    const handleOrderReferenceChanged = (e: FocusEvent<HTMLInputElement>): void => {
        const value = e.target.value.length ? e.target.value : null;
        dispatch(setOrderReference(value));
    };

    const handleDeliveryDateChanged = (date: Dayjs | null): void => {
        if (date === null || (date.isValid() && date.isAfter(dayjs()))) {
            dispatch(setDesiredDeliveryDate(date));
        }
    };

    const handleChangeDefaultSettings = (): void => {
        dispatch(
            changeDefaultSettings({
                deliveryAddressId: formik.values.deliveryAddressId,
                invoiceAddressId: formik.values.invoiceAddressId,
                avisoContactId: formik.values.avisoContactId === -1 ? null : formik.values.avisoContactId,
                costCentre: formik.values.costCentre ?? '',
            })
        );
    };

    return (
        <Grid container={true} spacing={2} columnSpacing={2}>
            <Grid item={true} xs={12}>
                <Box sx={{ mb: 1 }}>
                    <PoolTypography translationId="app.cart.orderData" variant="h5" />
                </Box>
            </Grid>
            <Grid item={true} container={true} sm={8} xs={12} spacing={2}>
                <Grid item={true} xs={12}>
                    <PoolFormikAutocomplete
                        name={fieldNames.deliveryAddressId}
                        label={<FormattedMessage id="app.cart.orderData.deliveryAddress" />}
                        options={deliveryAddressOptions}
                        required={true}
                        onChange={handleDeliveryAddressChanged}
                        endAdornment={
                            isDefaultDeliveryAddress ? (
                                <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                                    <StarIcon />
                                </StarContainer>
                            ) : null
                        }
                    />
                </Grid>
                <Grid item={true} xs={12}>
                    <PoolFormikAutocomplete
                        name={fieldNames.invoiceAddressId}
                        label={<FormattedMessage id="app.cart.orderData.invoiceAddress" />}
                        options={invoiceAddressOptions}
                        required={true}
                        onChange={handleInvoiceAddressChanged}
                        endAdornment={
                            isDefaultInvoiceAddress ? (
                                <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                                    <StarIcon />
                                </StarContainer>
                            ) : null
                        }
                    />
                </Grid>
            </Grid>
            <Grid item={true} container={true} sm={4} xs={12} spacing={2}>
                <Grid container={true} item={true} xs={12} spacing={2}>
                    <Grid item={true} sm={6} xs={12}>
                        <Box>
                            <FormikPoolDatePicker
                                name={fieldNames.deliveryDate}
                                setTimeToMidday={true}
                                minDate={dayjs()}
                                label={<FormattedMessage id="app.cart.orderData.deliveryDate" />}
                                onChange={handleDeliveryDateChanged}
                            />
                        </Box>
                    </Grid>

                    <Grid item={true} sm={6} xs={12}>
                        <PoolFormikTextfield
                            name={fieldNames.costCentre}
                            label={<FormattedMessage id="app.cart.orderData.costCentre" />}
                            fullWidth={true}
                            sx={{ minWidth: '100%' }}
                            onBlur={handleCostCentreChanged}
                            endAdornment={
                                isDefaultCostCentre ? (
                                    <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                                        <StarIcon />
                                    </StarContainer>
                                ) : null
                            }
                        />
                    </Grid>
                </Grid>
                <Grid item={true} container={true} xs={12} spacing={2}>
                    <Grid item={true} xs={12}>
                        <PoolFormikTextfield
                            name={fieldNames.orderReference}
                            label={<FormattedMessage id="app.cart.orderData.orderReference" />}
                            fullWidth={true}
                            sx={{ minWidth: '100%' }}
                            onBlur={handleOrderReferenceChanged}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item={true} sm={4} xs={12} spacing={2}>
                <PoolFormikAutocomplete
                    name={fieldNames.avisoContactId}
                    label={<FormattedMessage id="app.cart.orderData.aviso" />}
                    fullWidth={true}
                    onChange={handleAvisoContactChanged}
                    options={contactOptions}
                    endAdornment={
                        isDefaultAvisoContact ? (
                            <StarContainer title={<FormattedMessage id="app.tooltip.star" />}>
                                <StarIcon />
                            </StarContainer>
                        ) : null
                    }
                />
            </Grid>
            <Grid item={true} sm xs={12}>
                {selectedAvisoContact && (
                    <PoolRow sx={{ mt: { sm: 0.5 }, ml: { sm: 2.5 } }}>
                        <PoolAvatar
                            sx={{ width: 48, height: 48, fontSize: '1.375rem' }}
                            firstName={selectedAvisoContact?.firstName}
                            lastName={selectedAvisoContact?.lastName}
                            styleType="contain"
                        />
                        <Stack typography="body2">
                            {selectedAvisoContact.phone && (
                                <PoolLink href={`tel:${selectedAvisoContact.phone}`}>
                                    {selectedAvisoContact.phone}
                                </PoolLink>
                            )}
                            <PoolLink href={`mailto:${selectedAvisoContact.email}`} sx={{ overflow: 'hidden' }}>
                                {selectedAvisoContact.email}
                            </PoolLink>
                        </Stack>
                    </PoolRow>
                )}
            </Grid>
            <Grid item={true} sm="auto" xs={12} justifyContent={{ xs: 'inherit', sm: 'flex-end' }}>
                <PoolButton variant="text" color="dark" startIcon={<StarIcon />} onClick={handleChangeDefaultSettings}>
                    <FormattedMessage id="app.cart.orderData.standardSetting" />
                </PoolButton>
            </Grid>
        </Grid>
    );
};
